import { JobState } from "@eai-toolkit/client";
import {
  BlockOutlined,
  CheckCircleOutline,
  Close,
  ErrorOutline,
  InfoOutlined,
  OpenInNew,
} from "@mui/icons-material";
import type { SvgIcon } from "@mui/material";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import { useEffect, useMemo, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  DetailContainer,
  DetailTitle,
  ErrorPopoverContent,
  StateActionWrapper,
  StateDescription,
  StateInfoAndActionSection,
  StateSection,
  StatusBox,
  StatusBoxDivider,
  StyledStateIcon,
  TableHeadRow,
} from "@components/DetailStatusBox/DetailStatusBoxStyles";
import ResourceAccessErrorPanel from "@components/ResourceAccessErrorPanel";

import {
  getJobDetailUrl,
  getJobLogsUrl,
  JOB_DETAIL_SEGMENT,
  JOB_LOGS_SEGMENT,
} from "@helpers/constants";
import { JOBS_EVENTS } from "@helpers/events";
import { hasKey } from "@helpers/utils";

import FieldDisplay from "../../../data/FieldDisplay";
import { getErrorMessage } from "../../../store/api/baseApi";
import {
  useCancelJobMutation,
  useGetJobByIdQuery,
  useRetryJobMutation,
} from "../../../store/api/jobsApi";
import { getResourceErrorStatus } from "../../../store/api/types";
import { useAppSelector } from "../../../store/hooks";
import { selectNewJobForm } from "../../../store/slices/newJobFormSlice";
import { lightenWithRgba } from "../../../utils/colorUtils";
import fieldRenderers from "../data/fieldRenderers";
import { fields, jobFieldLabels } from "../data/fields";
import {
  getJobResourceUsageUrl,
  getJobWebAccessUrl,
  getStateBackgroundColor,
  jobStateDescription,
} from "../utils";
import JobDetailInfo from "./JobDetailInfo";
import JobDetailLogs from "./JobDetailLogs";

const columns = [
  fields.id,
  fields.account,
  fields.elapsed,
  fields.created,
  fields.endedOn,
  fields.creator,
];

const JobDetailContainer = styled(DetailContainer)({
  height: "100%",
  minHeight: 0,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
});

const DisabledCancelButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "alive",
})<{ alive?: boolean }>(({ theme, alive }) => ({
  "$container &": {
    color: alive
      ? lightenWithRgba(theme.palette.error.main)
      : lightenWithRgba(theme.palette.primary.main),
  },
}));

const PermissionNote = styled("div")(() => ({
  position: "relative",
  width: 150,
  "& > svg": {
    position: "absolute",
    top: 0,
    right: `calc(100% + 4px)`,
  },
  "& p": {
    fontStyle: "italic",
  },
  "& code": {
    fontFamily: '"Courier New", monospace',
    fontWeight: "bold",
  },
}));

const DetailTable = styled(Table)(({ theme }) => ({
  tableLayout: "fixed",
  width: "100%",
  minWidth: 0,
  "& th, & td": {
    paddingTop: 0,
    paddingBottom: 0,
    borderWidth: 0,
    padding: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    "&:first-child /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */":
      {
        paddingLeft: theme.spacing(1),
      },
    "&:last-child /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */":
      {
        paddingRight: theme.spacing(1),
      },
  },
}));

const DetailSummaryRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  alignItems: "end",
  gap: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1),
  },
}));

const DetailActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    justifyContent: "flex-start",
  },
}));

const TabIndicator = styled(Tabs)(({ theme }) => ({
  indicator: {
    backgroundColor: theme.palette.text.primary,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "unset",
  "&$tabSelected": {
    color: theme.palette.text.primary,
  },
}));

const SubroutesDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SubrouteContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "$logs",
})<{ $logs: boolean }>(({ $logs }) => ({
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: $logs ? "hidden" : "auto",
}));

function JobDetail() {
  // NOTE:
  // - jobIdentifier is for display
  // - idOrOtherwiseFullName is for looking up data

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { jobIdentifier } = useParams<{ jobIdentifier: string }>();
  const jobDetailPath = getJobDetailUrl(jobIdentifier ?? "");

  const idOrOtherwiseFullName = useMemo(() => {
    return location.state && "id" in location.state
      ? (location.state as { id: string }).id
      : jobIdentifier;
  }, [location.state, jobIdentifier]);

  const newJob = useAppSelector(selectNewJobForm);

  const [isTabVisible, setIsTabVisible] = useState(true);
  useEffect(() => {
    function handleVisibilityChange() {
      setIsTabVisible(document.visibilityState !== "hidden");
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const { data: { data: job, jobSetStatus } = {}, error: queryError } =
    useGetJobByIdQuery(
      { jobId: idOrOtherwiseFullName || "" },
      {
        pollingInterval: 10000,
        skip: !idOrOtherwiseFullName || !isTabVisible,
      },
    );

  const [
    cancelJobMutation,
    { isLoading: isCanceling, error: cancelMutationError },
  ] = useCancelJobMutation();
  const [
    retryJobMutation,
    { isLoading: isRetrying, error: retryMutationError },
  ] = useRetryJobMutation();

  const error = useMemo(() => {
    if (!queryError) return undefined;
    return getResourceErrorStatus(queryError);
  }, [queryError]);

  const match = useMatch(`/${JOB_DETAIL_SEGMENT}/:jobIdentifier/*`);

  useEffect(() => {
    if (job) {
      const { id, fullName } = job;
      const newMatchUrl = match?.pattern?.path.replace(
        ":jobIdentifier",
        fullName || id,
      );
      if (newMatchUrl && newMatchUrl !== match?.pathname) {
        navigate(
          location.pathname.replace(match?.pathname ?? "", newMatchUrl),
          {
            replace: true,
            state: {
              ...location.state,
              id,
            },
          },
        );
      }
    }
  }, [job, match, navigate, location.state, location.pathname]);

  // TODO: put this key in the API client spec
  const processAgent =
    hasKey(job, "processAgent") && typeof job.processAgent === "string"
      ? job.processAgent
      : undefined;
  // Grab process agent's permissions if there is one, since we can't directly
  // look up permissions on the process agent child job
  const { data: processAgentData } = useGetJobByIdQuery(
    { jobId: processAgent || "" },
    { skip: !processAgent },
  );

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  {
    const hasJob = Boolean(job);
    useEffect(() => {
      if (!hasJob) {
        setShowErrorSnackbar(false);
      } else {
        setShowErrorSnackbar(Boolean(error));
      }
    }, [hasJob, error]);
  }

  const [cancelButtonRef, setCancelButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const stateBusy = job
    ? job.state === JobState.CANCELLING || isCanceling || isRetrying
    : false;

  const cancelError = getErrorMessage(cancelMutationError);
  const retryError = getErrorMessage(retryMutationError);

  const [cancelRetryErrorOpen, setCancelRetryErrorOpen] = useState(false);
  const [cancelRetryErrorMessage, setCancelRetryErrorMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (cancelError || retryError) {
      setCancelRetryErrorMessage(cancelError || retryError || null);
      setCancelRetryErrorOpen(true);
    }
  }, [cancelError, retryError]);

  const handleCloseErrorDialog = () => {
    setCancelRetryErrorOpen(false);
    setCancelRetryErrorMessage(null);
  };

  // Use process agent's permission status if available, otherwise use the job's own status
  const effectiveJobSetStatus = processAgent
    ? processAgentData?.jobSetStatus
    : jobSetStatus;

  const jobSetLookupCompleted = Boolean(effectiveJobSetStatus);
  const jobSetAllowed = effectiveJobSetStatus === "ok";

  const showingLogs = location.pathname.endsWith(`/${JOB_LOGS_SEGMENT}`);

  return (
    <JobDetailContainer>
      <DetailTitle variant="h2">
        <span>Job detail</span>
        <span>
          {job
            ? job.name || job.id
            : // the URL contains fullName or ID, so to extract the name we can
              // look for whatever comes after the last '.'
              jobIdentifier?.split(".")?.pop() || ""}
        </span>
      </DetailTitle>
      {!job ? (
        error ? (
          <ResourceAccessErrorPanel
            status={error}
            resourceType="job"
            resourceIdentifier={idOrOtherwiseFullName}
            backTo="/"
            backLabel="View all jobs"
            backButtonVariant={error === "no_access" ? "outlined" : "contained"}
            titleIcon={<ErrorOutline color="error" />}
          />
        ) : // TODO: implement loading state if fetch is taking awhile?
        null
      ) : (
        <>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            open={showErrorSnackbar}
            message="We had trouble requesting updated job info"
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => setShowErrorSnackbar(false)}
              >
                <Close fontSize="small" />
              </IconButton>
            }
          />
          <StatusBox>
            <StateSection>
              <StyledStateIcon
                style={{
                  color: getStateBackgroundColor(job.state, theme.palette),
                }}
              >
                {(() => {
                  if (
                    !job.state ||
                    job.state === JobState.QUEUING ||
                    job.state === JobState.QUEUED ||
                    job.state === JobState.RUNNING
                  ) {
                    return (
                      <CircularProgress
                        size={theme.spacing(9)}
                        color="inherit"
                      />
                    );
                  }
                  let StateIcon: typeof SvgIcon;
                  switch (job.state) {
                    case JobState.SUCCEEDED:
                      StateIcon = CheckCircleOutline;
                      break;
                    case JobState.FAILED:
                    case JobState.INTERRUPTED:
                      StateIcon = ErrorOutline;
                      break;
                    case JobState.CANCELLED:
                    case JobState.CANCELLING:
                      StateIcon = BlockOutlined;
                      break;
                  }
                  return <StateIcon color="inherit" fontSize="inherit" />;
                })()}
              </StyledStateIcon>
              <StateDescription>
                <Typography variant="h3">
                  {newJob.state === "ERROR_AFTER_SUBMIT"
                    ? "Job failed to start successfully"
                    : jobStateDescription(job.state)}
                </Typography>
                <FieldDisplay
                  fieldValue={job.state}
                  dataType="State"
                  fieldRenderers={fieldRenderers}
                />
              </StateDescription>
            </StateSection>
            <StatusBoxDivider />
            <StateInfoAndActionSection>
              <Typography variant="body1" color="textSecondary">
                {(job.stateInfo ||
                  job.state === JobState.QUEUED ||
                  job.state === JobState.QUEUING) &&
                  (job.stateInfo || "Waiting for more info...")}
              </Typography>
              <StateActionWrapper
                hidden={job.state === JobState.SUCCEEDED}
                hasAdjacentNote={jobSetLookupCompleted && !jobSetAllowed}
              >
                <DisabledCancelButton
                  alive={job.alive}
                  color={job.alive ? "inherit" : "primary"}
                  type="button"
                  disabled={stateBusy || !jobSetAllowed}
                  onClick={() => {
                    if (job.alive) {
                      tracker.event(JOBS_EVENTS.DETAIL_CANCEL_CLICKED, job.id);
                      cancelJobMutation({ jobId: job.id });
                    } else {
                      tracker.event(JOBS_EVENTS.DETAIL_RETRY_CLICKED, job.id);
                      retryJobMutation({ jobId: job.id });
                    }
                  }}
                  ref={(ref) => setCancelButtonRef(ref)}
                >
                  {job.alive ? "Cancel" : "Retry"}
                </DisabledCancelButton>
                <Popover
                  open={cancelRetryErrorOpen}
                  anchorEl={cancelButtonRef}
                  onClose={handleCloseErrorDialog}
                  anchorOrigin={{
                    vertical: "center",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "center",
                    horizontal: "right",
                  }}
                >
                  <ErrorPopoverContent>
                    <Typography variant="subtitle2" className="error-label">
                      {cancelError ? "Error on cancel:" : "Error on retry:"}
                    </Typography>
                    <Typography variant="body2" className="error-message">
                      {cancelRetryErrorMessage &&
                        `"${cancelRetryErrorMessage.slice(0, 250)}${
                          cancelRetryErrorMessage.length > 250 ? "..." : ""
                        }"`}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="close"
                      className="close-button"
                      onClick={handleCloseErrorDialog}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </ErrorPopoverContent>
                </Popover>
                {jobSetLookupCompleted && !jobSetAllowed && (
                  <PermissionNote>
                    <InfoOutlined color="secondary" />
                    <Typography variant="body2" color="textSecondary">
                      You can&apos;t {job.alive ? "cancel" : "retry"} since you
                      don&apos;t have <code>set</code> permission for this job
                    </Typography>
                  </PermissionNote>
                )}
              </StateActionWrapper>
            </StateInfoAndActionSection>
          </StatusBox>
          <DetailSummaryRow>
            <DetailTable>
              <TableHead>
                <TableHeadRow>
                  {columns.map((col) => (
                    <TableCell
                      scope="col"
                      key={col.fieldName}
                      style={
                        col.fieldName === "id"
                          ? { width: 120, border: "none" }
                          : undefined
                      }
                    >
                      {jobFieldLabels[col.fieldName]}
                    </TableCell>
                  ))}
                </TableHeadRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.fieldName}
                      align="left"
                      component={col.fieldName === "id" ? "th" : "td"}
                      scope={col.fieldName === "id" ? "row" : undefined}
                    >
                      <FieldDisplay
                        fieldValue={job[col.fieldName]}
                        dataType={col.dataType}
                        fieldRenderers={fieldRenderers}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </DetailTable>
            <DetailActions>
              <Button
                sx={{
                  disabled: {
                    "$container &": {
                      color: lightenWithRgba(theme.palette.primary.main),
                    },
                  },
                }}
                color="primary"
                type="button"
                disabled={job.state !== JobState.RUNNING}
                endIcon={<OpenInNew />}
                component="a"
                href={getJobWebAccessUrl(job)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  tracker.event(JOBS_EVENTS.DETAIL_WEB_ACCESS_CLICKED)
                }
              >
                Web access
              </Button>
              <Button
                sx={{
                  disabled: {
                    "$container &": {
                      color: lightenWithRgba(theme.palette.primary.main),
                    },
                  },
                }}
                color="primary"
                type="button"
                endIcon={<OpenInNew />}
                component="a"
                href={getJobResourceUsageUrl(job)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  tracker.event(
                    JOBS_EVENTS.DETAIL_RESOURCE_USAGE_CLICKED,
                    job.id,
                  )
                }
              >
                Resource usage
              </Button>
            </DetailActions>
          </DetailSummaryRow>
          <TabIndicator
            value={showingLogs ? "logs" : "info"}
            indicatorColor="primary"
            textColor="primary"
          >
            <StyledTab
              label="Job info"
              value="info"
              onClick={() => {
                tracker.event(JOBS_EVENTS.DETAIL_TAB_CHANGED, "info");
                navigate(jobDetailPath, {
                  replace: true,
                  state: job && { id: job.id },
                });
              }}
              sx={{
                textTransform: "unset",
                "&.Mui-selected": {
                  color: theme.palette.text.primary,
                },
              }}
            />
            <StyledTab
              label="Logs"
              value="logs"
              onClick={() => {
                tracker.event(JOBS_EVENTS.DETAIL_TAB_CHANGED, "logs");
                navigate(getJobLogsUrl(jobIdentifier ?? ""), {
                  replace: true,
                  state: job && { id: job.id },
                });
              }}
              // TODO - make sure the selected thing works
              sx={{
                textTransform: "unset",
                "&.Mui-selected": {
                  color: theme.palette.text.primary,
                },
              }}
            />
          </TabIndicator>
          <SubroutesDivider />
        </>
      )}
      {job && (
        <SubrouteContent $logs={showingLogs}>
          <Routes>
            <Route path="/" element={<JobDetailInfo job={job} />} />
            <Route
              path={`/${JOB_LOGS_SEGMENT}`}
              element={<JobDetailLogs job={job} />}
            />
          </Routes>
        </SubrouteContent>
      )}
    </JobDetailContainer>
  );
}

export default JobDetail;
